import React, { useEffect, useState } from 'react';
import { DndProvider, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import StoreController, { HotelIntroType } from '../../../controller/StoreController';
import toast from 'react-hot-toast';
import DetectorController, { HotelListType } from '../../../controller/DetectorController';
import Select from 'react-select';

const RecommendIndex = () => {
  const navigate = useNavigate();

  const [hotelList, setHotelList] = useState<HotelListType[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<HotelListType | null>(null);
  const [guideList, setGuideList] = useState<HotelIntroType[]>([]);

  const navigateGuideInfo = async (guideItem?: HotelIntroType) => {
    if (selectedHotel) {
      if (guideItem) {
        navigate(`/knock/recommend/edit/${selectedHotel.store_id}/${guideItem.card_id}`);
      } else navigate(`/knock/recommend/edit/${selectedHotel.store_id}/-1`);
    }
  };

  // API 호출
  const getHotelList = async () => {
    try {
      const response = await DetectorController.getHotelList();

      if (response && response.data) {
        const formattedHotelList = response.data.data.map((item: any) => ({
          value: item.store_name,
          label: item.store_name,
          store_id: item.store_id,
          user_id: item.user_id,
          bcode_list: item.bcode_list,
        }));
        setHotelList(formattedHotelList);
        if (selectedHotel === null) {
          setSelectedHotel(formattedHotelList[0] || null);
        }
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };

  const getHotelIntro = async () => {
    try {
      if (selectedHotel) {
        const oParams = {
          store_id: selectedHotel.store_id,
          card_type: 1,
        };

        const oRes = await StoreController.getHotelIntro(oParams);
        if (oRes && oRes.data) {
          const oResponse = oRes.data;
          console.log('getHotelIntro', oResponse);
          if (oResponse.data && oResponse.ok) {
            setGuideList(oResponse.data);
          }
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };

  const editHotelIntroIdOrder = async () => {
    try {
      if (selectedHotel) {
        const oParams = {
          store_id: selectedHotel.store_id,
          hotel_intro: guideList,
        };

        const oRes = await StoreController.editHotelIntroIdOrder(oParams);
        if (oRes && oRes.data) {
          const oResponse = oRes.data;
          console.log('editHotelIntroIdOrder', oResponse);
          if (oResponse.data && oResponse.ok) {
            toast.success('호텔소개 순서를 변경했어요');
          }
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };

  const handleChange = (selectedOption: HotelListType | null) => {
    setSelectedHotel(selectedOption);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 1) toIndex = 0;
    else if (toIndex > guideList.length - 1) toIndex = guideList.length;
    const newItems = [...guideList];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    setGuideList(newItems);
  };

  useEffect(() => {
    getHotelList();
  }, []);

  useEffect(() => {
    if (selectedHotel !== null) {
      getHotelIntro();
    }
  }, [selectedHotel]);

  interface DraggableItemProps {
    item: HotelIntroType;
    index: number;
    moveItem: (fromIndex: number, toIndex: number) => void;
  }

  const RenderGuideItem = ({ item, index, moveItem }: DraggableItemProps) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(index + 1);

    const [, drag] = useDrag({
      type: 'ITEM',
      item: { item, index },
    });

    const [, drop] = useDrop({
      accept: 'ITEM',
      hover: (item: { index: number }, monitor: DropTargetMonitor) => {
        if (item.index !== index) {
          moveItem(item.index, index);
          item.index = index;
        }
      },
    });

    return (
      <div key={index}>
        <div className={`listItem`}>
          <div className="row gap">
            <input
              ref={(node) => drag(drop(node))}
              value={selectedIndex}
              autoFocus={false}
              onChange={(e) => {
                setSelectedIndex(Number(e.target.value));
              }}
              onBlur={() => {
                moveItem(index, selectedIndex - 1);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  moveItem(index, selectedIndex - 1);
                }
              }}
            />
            <div className="imgWrap">
              {item.url !== null && item.url !== '' && <img src={item.url} alt="guide_logo" />}
            </div>
            <h3>{item.title}</h3>
          </div>
          <button className="editButton" onClick={() => navigateGuideInfo(item)}>
            수정하기
          </button>
        </div>
      </div>
    );
  };

  return (
    <Style>
      <div className="contentContainer">
        <button className="backButton" onClick={() => navigate('/dashboard')}>
          <img src={`${process.env.PUBLIC_URL}/svg/ic_home.svg`} alt="ic" />
        </button>
        <div className="header">
          <div className="selectWrap">
            <Select options={hotelList} value={selectedHotel} onChange={handleChange} />
          </div>
        </div>
        <button className="addButton" onClick={() => navigateGuideInfo()}>
          <img src={`${process.env.PUBLIC_URL}/svg/ic_plus.svg`} alt="ic" />
          <h4>추천매장 추가하기</h4>
        </button>
        <div className="warningSign">
          <img src={`${process.env.PUBLIC_URL}/svg/ic_warning.svg`} alt="ic" />
          <p>숫자를 입력하거나 숫자 버튼을 드래그 해서 순서를 변경해주세요.</p>
        </div>

        <DndProvider backend={HTML5Backend}>
          {guideList.length > 0 ? (
            <div className="categoryList">
              {guideList.map((item: HotelIntroType, index: number) => {
                return (
                  <RenderGuideItem key={index} item={item} index={index} moveItem={moveItem} />
                );
              })}
            </div>
          ) : (
            <div className="categoryList none">
              <h3>추천매장을 등록해주세요</h3>
            </div>
          )}
        </DndProvider>

        <button className={`submitButton`} onClick={editHotelIntroIdOrder}>
          저장
        </button>
      </div>
    </Style>
  );
};

export default RecommendIndex;

const Style = styled.div`
  .backButton {
    width: fit-content;
    margin-bottom: 20px;
  }

  h3.title {
    font-weight: 600;
    color: #555555;
    margin-bottom: 30px;
  }

  .addButton {
    width: 100%;
    background: #fff;
    border: 1px solid #4dd2ae;
    border-radius: 5px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5%;
    margin: 10px 0;

    h4 {
      font-weight: 600;
      color: #4dd2ae;
      white-space: nowrap;
      padding: 0;
    }
  }

  .categoryList {
    min-height: 40dvh;
    padding: 10px 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .listItem {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 40px 10px 20px;
      border: 1px solid #e5e5e5;
      border-radius: 5px;

      input {
        width: 30px;
        height: 30px;
        padding: 4px;
        font-size: 16px;
        line-height: 20px;
        font-weight: 600;
        text-align: center;
        color: #4dd2ae;
        background: #ffffff;
        border: 1px solid #4dd2ae;
        border-radius: 4px;
      }

      .row.gap {
        gap: 14px;
      }
      h3 {
        font-size: 18px;
        color: #555555;
        font-weight: 600;
      }

      .imgWrap {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        border-radius: 5px;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 5px;
        }
      }

      .editButton {
        background: #ffffff;
        text-align: center;
        border-radius: 5px;
        padding: 6px 14px;
        font-size: 14px;
        line-height: 18px;
        font-weight: 500;
        border: 1px solid #cecece;
        color: #979797;
        cursor: pointer;
      }
    }
  }

  .categoryList.none {
    text-align: center;
    justify-content: center;
    h3 {
      color: #aaaaaa;
    }
  }

  .submitButton {
    width: 100%;
    margin-top: 50px;
    padding: 12px 0;
    background: #4dd2ae;
    color: #fff;
    border: none;
    outline: none;
    border-radius: 8px;
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
  }

  .submitButton.inactive {
    background: #cecece;
  }

  .warningSign {
    padding: 4px 0;
    display: flex;
    align-items: flex-start;
    gap: 6px;

    p {
      font-size: 14px;
      line-height: 16px;
      color: #979797;
    }
    span {
      font-size: 12px;
      color: #979797;
      font-weight: 400;
    }
  }

  @media (max-width: 800px) {
    .contentContainer {
      width: 100%;
      margin: 0 auto;
      height: 100dvh;
      padding: 4%;
      background: #ffffff;
      border-radius: 10px;
    }

    .categoryList {
      gap: 20px;

      .listItem {
        padding: 8px 10px 8px 16px;

        input {
          width: 26px;
          height: 26px;
          font-size: 14px;
        }
        h3 {
          font-size: 16px;
          color: #555555;
          font-weight: 600;
        }
      }
    }
  }
`;
